/*
 * FractalAI bilingual file header.
 *
 * 文件职责 / File Purpose
 * - 中文：前端共享工具模块。提供路由、资产类型、图表偏好、时间周期、格式化和数据适配等跨页面复用能力。
 * - English: Frontend shared utility module. Provides shared routing, asset-type, chart preference, resolution, formatting, and data-adapter helpers.
 *
 * 调用关系 / Call Flow
 * - 被调用 / Called by: 由多个页面、组件、验证路由和图表引擎调用。
 * - Called by (EN): Called by multiple pages, components, validation routes, and chart engines.
 * - 主要依赖 / Main deps: dayjs, dayjs/plugin/weekday, dayjs/plugin/localeData, dayjs/plugin/advancedFormat, dayjs/plugin/customParseFormat, dayjs/plugin/weekOfYear, dayjs/plugin/isSameOrAfter, dayjs/plugin/isSameOrBefore
 * - 接口/服务信号 / API or service signals: 未在文件内直接声明 endpoint 或 ctx.service 调用。
 * - 关键符号 / Key symbols: nativeWeek, nativeDaysInMonth
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：工具函数应保持纯净可测试；资产类型逻辑必须与 BFF 版本同步。
 * - English: Utilities should stay pure and testable; asset-type logic must stay aligned with the BFF version.
 *
 * 修改注意 / Change Notes
 * - 中文：修改公共工具前先搜索调用方，避免破坏兼容页面。
 * - English: Search callers before changing shared utilities to avoid breaking compatibility pages.
 */
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc'; // 新增UTC插件

// 加载所有必要插件
dayjs.extend(weekday);
dayjs.extend(utc); // 启用UTC支持
dayjs.extend(localeData);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const nativeWeek = dayjs.prototype.week;
const nativeDaysInMonth = dayjs.prototype.daysInMonth;

// 完整实现 moment.js API 兼容
dayjs.prototype.clone = function() {
  return dayjs(this.toDate());
};

dayjs.prototype.weekday = function(value) {
  if (value !== undefined) {
    return this.day(value);
  }
  return this.day();
};

dayjs.prototype.weekYear = function() {
  return this.week();
};

// 添加 Ant Design 需要的其他方法
dayjs.prototype.monthsShort = function() {
  return dayjs.localeData().monthsShort();
};

dayjs.prototype.creationData = function() {
  return {
    input: this.toISOString(),
    format: undefined,
    locale: dayjs.locale(),
    isUTC: false
  };
};

// 添加更多兼容方法
// 实现更多 Ant Design 需要的 moment.js 方法
dayjs.prototype.week = function(value) {
  if (typeof nativeWeek === 'function') {
    return nativeWeek.call(this, value);
  }
  return value === undefined ? 0 : this;
};

dayjs.prototype.weeks = function() {
  return this.week();
};

dayjs.prototype.weekYear = function() {
  return this.week();
};

dayjs.prototype.daysInMonth = function() {
  if (typeof nativeDaysInMonth === 'function') {
    return nativeDaysInMonth.call(this);
  }
  return new Date(this.year(), this.month() + 1, 0).getDate();
};

dayjs.prototype.isSameOrAfter = function(date, unit) {
  return this.isSame(date, unit) || this.isAfter(date, unit);
};

dayjs.prototype.isSameOrBefore = function(date, unit) {
  return this.isSame(date, unit) || this.isBefore(date, unit);
};

// 添加 getWeekDay 方法
dayjs.prototype.getWeekDay = function() {
  return this.day();
};

// 覆盖全局 Date 对象的方法
Date.prototype.weekday = function() {
  return dayjs(this).day();
};

// 覆盖为UTC模式的moment对象
if (typeof window !== 'undefined') {
  window.moment = (date) => dayjs(date).utc();
}

export default dayjs;
